import {
  BATTLESHIP_CELL_COUNT,
  type BattleshipEvent,
  type BattleshipOrientation,
  type BattleshipPublicBoard,
  type BattleshipPublicShip,
  type BattleshipPublicState,
  type BattleshipShipId,
  battleshipFleet,
  getBattleshipPlacementCells,
  isBattleshipFleetComplete,
  isBattleshipPlacementLegal,
} from "@topzle/game-battleship";
import {
  Activity,
  Crosshair,
  Gauge,
  Grid3X3,
  Grip,
  LoaderCircle,
  LocateFixed,
  Radar,
  RotateCw,
  ShieldCheck,
  Ship,
  Shuffle,
  Volume2,
  VolumeX,
  Waves,
  X,
} from "lucide-react";
import {
  type CSSProperties,
  type DragEvent,
  type ReactNode,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  type BattleshipAttackStyle,
  playBattleshipAttackSound,
  setBattleshipAudioEnabled,
} from "../battleshipSfx";

interface BattleshipBoardProps {
  state: BattleshipPublicState;
  participantId: string;
  pending: boolean;
  serverError: string | null;
  playAgain: ReactNode;
  onAction: (action: BattleshipEvent) => void;
}

interface PlacementControls {
  selectedShipId: BattleshipShipId;
  orientation: BattleshipOrientation;
  hoverIndex: number | null;
  onHover: (index: number | null) => void;
  onSelectShip: (shipId: BattleshipShipId) => void;
  onPlaceShip: (index: number, shipId?: BattleshipShipId) => void;
}

function playerLabel(playerId: string, participantId: string): string {
  if (playerId === participantId) return "You";
  if (playerId.startsWith("ai:")) return "Topzle AI";
  return "Opponent";
}

function coordinate(index: number): string {
  return `${String.fromCharCode(65 + (index % 10))}${Math.floor(index / 10) + 1}`;
}

const navalCells = Array.from({ length: BATTLESHIP_CELL_COUNT }, (_, index) => ({
  index,
  coordinate: coordinate(index),
}));
const navalRows = Array.from({ length: 10 }, (_, index) => String(index + 1));
const navalHullBlocks = ["bow", "forward", "midship", "aft", "stern"] as const;
const shipArtwork: Record<BattleshipShipId, string> = {
  carrier: "/assets/ships/carrier.svg",
  battleship: "/assets/ships/destroyer.svg",
  cruiser: "/assets/ships/destroyer.svg",
  submarine: "/assets/ships/submarine.svg",
  destroyer: "/assets/ships/destroyer.svg",
};

function shipOrientation(ship: BattleshipPublicShip): BattleshipOrientation {
  return ship.cells.length > 1 && Math.abs((ship.cells[1] ?? 0) - (ship.cells[0] ?? 0)) === 10
    ? "vertical"
    : "horizontal";
}

function resolvePlacement(
  ships: BattleshipPublicShip[],
  shipId: BattleshipShipId,
  startIndex: number,
  preferredOrientation: BattleshipOrientation,
) {
  const definition = battleshipFleet.find((ship) => ship.id === shipId);
  if (!definition) return { cells: null, orientation: preferredOrientation, legal: false } as const;
  const preferredCells = getBattleshipPlacementCells(
    startIndex,
    definition.length,
    preferredOrientation,
  );
  if (isBattleshipPlacementLegal(ships, shipId, preferredCells))
    return { cells: preferredCells, orientation: preferredOrientation, legal: true } as const;
  const alternateOrientation = preferredOrientation === "horizontal" ? "vertical" : "horizontal";
  const alternateCells = getBattleshipPlacementCells(
    startIndex,
    definition.length,
    alternateOrientation,
  );
  if (isBattleshipPlacementLegal(ships, shipId, alternateCells))
    return { cells: alternateCells, orientation: alternateOrientation, legal: true } as const;
  return { cells: preferredCells, orientation: preferredOrientation, legal: false } as const;
}

function ShipSilhouette({
  ship,
  ownerId,
  selected = false,
  preview = false,
}: {
  ship: BattleshipPublicShip;
  ownerId: string;
  selected?: boolean;
  preview?: boolean;
}) {
  if (ship.cells.length === 0) return null;
  const orientation = shipOrientation(ship);
  const startIndex = Math.min(...ship.cells);
  const row = Math.floor(startIndex / 10);
  const column = startIndex % 10;
  const style = {
    "--ship-row": row,
    "--ship-column": column,
    "--ship-length": ship.length,
  } as CSSProperties;
  return (
    <span
      className={[
        "naval-ship",
        `ship-${ship.id}`,
        orientation,
        ship.sunk ? "sunk" : "",
        selected ? "selected" : "",
        preview ? "preview" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      style={style}
      data-battleship-ship={`${ownerId}:${ship.id}`}
      aria-hidden="true"
    >
      <img className="ship-art ship-art-base" src={shipArtwork[ship.id]} alt="" />
      {ship.sunk && (
        <>
          <img className="ship-art wreck-piece wreck-piece-a" src={shipArtwork[ship.id]} alt="" />
          <img className="ship-art wreck-piece wreck-piece-b" src={shipArtwork[ship.id]} alt="" />
          <i className="wreck-fire" />
          <i className="wreck-smoke" />
        </>
      )}
    </span>
  );
}

interface MissilePath {
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
  arc: number;
  fromFleetStatus: boolean;
}

const attackDetails: Record<
  BattleshipAttackStyle,
  { label: string; description: string; duration: number; impactDelay: number }
> = {
  carrier: {
    label: "Carrier air raid",
    description: "Launches a strike aircraft that dives onto the selected enemy coordinate.",
    duration: 1.18,
    impactDelay: 1.44,
  },
  battleship: {
    label: "Surface missile strike",
    description: "Fires a heavy guided missile across the board for a powerful direct impact.",
    duration: 1.02,
    impactDelay: 1.02,
  },
  cruiser: {
    label: "Rapid naval-gun barrage",
    description: "Delivers a fast sequence of naval-gun rounds at the selected coordinate.",
    duration: 0.68,
    impactDelay: 1.13,
  },
  submarine: {
    label: "Heavy torpedo run",
    description: "Sends a concealed torpedo through the water before it strikes from below.",
    duration: 1.3,
    impactDelay: 1.3,
  },
  destroyer: {
    label: "Asymmetric swarm assault",
    description: "Deploys a coordinated drone swarm that converges on the target.",
    duration: 1.04,
    impactDelay: 1.06,
  },
};

const shipClassDetails: Record<BattleshipShipId, { classification: string; role: string }> = {
  carrier: {
    classification: "Fleet aircraft carrier",
    role: "The fleet's largest vessel and long-range aviation command platform.",
  },
  battleship: {
    classification: "Heavy capital ship",
    role: "A heavily armoured surface combatant built for high-impact strikes.",
  },
  cruiser: {
    classification: "Guided-missile cruiser",
    role: "A fast multi-role warship designed for accurate surface bombardment.",
  },
  submarine: {
    classification: "Attack submarine",
    role: "A stealth vessel that attacks beneath the surface with heavy torpedoes.",
  },
  destroyer: {
    classification: "Fleet destroyer",
    role: "A small, agile escort vessel that uses coordinated asymmetric attacks.",
  },
};

export function getBattleshipShipStatus(
  ship: BattleshipPublicShip | undefined,
  deploymentView: boolean,
): "Unknown" | "Not deployed" | "Operational" | "Damaged" | "Sunk" {
  if (!ship) return "Unknown";
  if (ship.sunk) return "Sunk";
  if (deploymentView && ship.cells.length === 0) return "Not deployed";
  if (ship.hits.length > 0) return "Damaged";
  return "Operational";
}

export function selectAttackStyle(
  state: BattleshipPublicState,
  shot: NonNullable<BattleshipPublicState["lastShot"]>,
): BattleshipAttackStyle | null {
  const attacker = state.boards.find((board) => board.playerId === shot.attackerId);
  // Enemy coordinates are intentionally hidden in public state, so operational
  // weapon selection must rely on sunk status rather than visible ship cells.
  const operational = attacker?.ships.filter((ship) => !ship.sunk) ?? [];
  if (operational.length === 0) return null;
  const shotCount = state.boards.reduce((total, board) => total + board.shotsReceived.length, 0);
  const seed = shot.index * 31 + shotCount * 17 + state.round * 13;
  return operational[Math.abs(seed) % operational.length]?.id ?? null;
}

function shotPresentationKey(shot: NonNullable<BattleshipPublicState["lastShot"]>): string {
  return `${shot.attackerId}:${shot.defenderId}:${shot.index}`;
}

export function concealUnresolvedShot(
  board: BattleshipPublicBoard,
  shot: BattleshipPublicState["lastShot"],
  revealed: boolean,
): BattleshipPublicBoard {
  if (!shot || revealed || board.playerId !== shot.defenderId) return board;
  return {
    ...board,
    shotsReceived: board.shotsReceived.filter((candidate) => candidate.index !== shot.index),
    ships: board.ships.map((ship) =>
      ship.id === shot.shipId
        ? {
            ...ship,
            cells: shot.sunkShipId === ship.id && !board.fleetVisible ? [] : ship.cells,
            hits: ship.hits.filter((cell) => cell !== shot.index),
            sunk: shot.sunkShipId === ship.id ? false : ship.sunk,
          }
        : ship,
    ),
  };
}

function AttackFlight({ style }: { style: BattleshipAttackStyle }) {
  if (style === "carrier")
    return (
      <>
        {[0, 1, 2].map((index) => (
          <i
            className="attack-x-track carrier-plane-x"
            style={{ "--attack-delay": `${index * 0.13}s` } as CSSProperties}
            key={index}
          >
            <b className="attack-arc-track carrier-plane-arc">
              <span className="naval-attack-plane">
                <i className="plane-fuselage" />
                <i className="plane-wing wing-left" />
                <i className="plane-wing wing-right" />
                <i className="plane-afterburner" />
              </span>
            </b>
          </i>
        ))}
      </>
    );
  if (style === "cruiser")
    return (
      <>
        <i className="naval-muzzle-flash" />
        {[0, 1, 2, 3].map((index) => (
          <i
            className="attack-x-track cruiser-tracer-x"
            style={{ "--attack-delay": `${index * 0.15}s` } as CSSProperties}
            key={index}
          >
            <b className="attack-arc-track cruiser-tracer-arc">
              <span className="naval-tracer" />
            </b>
          </i>
        ))}
      </>
    );
  if (style === "submarine")
    return (
      <i className="attack-x-track torpedo-x">
        <b className="attack-arc-track torpedo-arc">
          <span className="naval-torpedo">
            <i className="torpedo-propeller" />
            <i className="torpedo-bubbles" />
          </span>
        </b>
      </i>
    );
  if (style === "destroyer")
    return (
      <>
        {[0, 1, 2, 3, 4, 5].map((index) => (
          <i
            className="attack-x-track swarm-x"
            style={
              {
                "--attack-delay": `${index * 0.075}s`,
                "--swarm-lane": `${(index - 2.5) * 13}px`,
              } as CSSProperties
            }
            key={index}
          >
            <b className="attack-arc-track swarm-arc">
              <span className="naval-swarm-drone">
                <i />
                <b />
              </span>
            </b>
          </i>
        ))}
      </>
    );
  return (
    <i className="attack-x-track missile-x-track">
      <b className="attack-arc-track missile-arc-track">
        <span className="naval-missile">
          <i className="missile-nose" />
          <i className="missile-window" />
          <i className="missile-fin fin-a" />
          <i className="missile-fin fin-b" />
          <i className="missile-flame" />
        </span>
      </b>
    </i>
  );
}

function GlobalShotSequence({
  shot,
  state,
  participantId,
  soundEnabled,
}: {
  shot: BattleshipPublicState["lastShot"];
  state: BattleshipPublicState;
  participantId: string;
  soundEnabled: boolean;
}) {
  const [path, setPath] = useState<MissilePath | null>(null);
  const attackStyle = shot ? selectAttackStyle(state, shot) : null;
  const details = attackStyle ? attackDetails[attackStyle] : null;

  useLayoutEffect(() => {
    if (!shot || !attackStyle) {
      setPath(null);
      return;
    }
    const escapedTarget = CSS.escape(`${shot.defenderId}:${shot.index}`);
    const target = document.querySelector<HTMLElement>(`[data-battleship-cell="${escapedTarget}"]`);
    if (!target) return;
    const sourceSelector =
      shot.attackerId === participantId
        ? `[data-battleship-ship="${CSS.escape(`${shot.attackerId}:${attackStyle}`)}"]`
        : `[data-battleship-launch-source="${CSS.escape(`${shot.attackerId}:${attackStyle}`)}"]`;
    const source = document.querySelector<HTMLElement>(sourceSelector);
    const targetRect = target.getBoundingClientRect();
    const sourceRect = source?.getBoundingClientRect();
    const sourceX = sourceRect ? sourceRect.left + sourceRect.width / 2 : window.innerWidth / 2;
    const sourceY = sourceRect
      ? sourceRect.top + sourceRect.height / 2
      : shot.attackerId.startsWith("ai:")
        ? 20
        : window.innerHeight - 20;
    const targetX = targetRect.left + targetRect.width / 2;
    const targetY = targetRect.top + targetRect.height / 2;
    setPath({
      sourceX,
      sourceY,
      targetX,
      targetY,
      arc: Math.max(80, Math.min(240, Math.hypot(targetX - sourceX, targetY - sourceY) * 0.28)),
      fromFleetStatus: shot.attackerId !== participantId,
    });
  }, [attackStyle, participantId, shot]);

  useEffect(() => {
    if (!shot || !attackStyle || !soundEnabled) return;
    void playBattleshipAttackSound(attackStyle, shot.result, Boolean(shot.sunkShipId));
  }, [attackStyle, shot, soundEnabled]);

  if (!shot || !path || !attackStyle || !details) return null;
  const angle =
    (Math.atan2(path.targetY - path.sourceY, path.targetX - path.sourceX) * 180) / Math.PI + 90;
  const style = {
    "--missile-source-x": `${path.sourceX}px`,
    "--missile-source-y": `${path.sourceY}px`,
    "--missile-dx": `${path.targetX - path.sourceX}px`,
    "--missile-dy": `${path.targetY - path.sourceY}px`,
    "--missile-arc": `${path.arc}px`,
    "--missile-target-x": `${path.targetX}px`,
    "--missile-target-y": `${path.targetY}px`,
    "--attack-mid-y": `${(path.targetY - path.sourceY) * 0.48 - path.arc}px`,
    "--torpedo-mid-y": `${(path.targetY - path.sourceY) * 0.48 + 12}px`,
    "--swarm-mid-y": `${(path.targetY - path.sourceY) * 0.52 - path.arc * 0.28}px`,
    "--attack-angle": `${angle}deg`,
    "--attack-duration": `${details.duration}s`,
    "--impact-delay": `${details.impactDelay}s`,
  } as CSSProperties;
  return (
    <span
      className={`battleship-global-shot weapon-${attackStyle} ${shot.result}${
        shot.sunkShipId ? " final-blow" : ""
      }${path.fromFleetStatus ? " from-fleet-status" : ""}`}
      style={style}
      aria-hidden="true"
    >
      <span className="naval-attack-caption">{details.label}</span>
      <i className="naval-launch-origin">
        <b />
        <b />
      </i>
      <AttackFlight style={attackStyle} />
      <i className="naval-global-impact">
        <b className="impact-core" />
        <b className="impact-ring ring-one" />
        <b className="impact-ring ring-two" />
        <b className="impact-particle particle-one" />
        <b className="impact-particle particle-two" />
        <b className="impact-particle particle-three" />
        <b className="impact-particle particle-four" />
      </i>
    </span>
  );
}

function NavalGrid({
  board,
  label,
  targetable,
  pending,
  compact = false,
  placement,
  onFire,
}: {
  board: BattleshipPublicBoard;
  label: string;
  targetable: boolean;
  pending: boolean;
  compact?: boolean;
  placement?: PlacementControls | undefined;
  onFire: (index: number) => void;
}) {
  const shipsByCell = new Map(
    board.ships.flatMap((ship) => ship.cells.map((cell) => [cell, ship] as const)),
  );
  const shotsByCell = new Map(board.shotsReceived.map((shot) => [shot.index, shot] as const));
  const selectedDefinition = placement
    ? battleshipFleet.find((ship) => ship.id === placement.selectedShipId)
    : undefined;
  const previewCells =
    placement && placement.hoverIndex !== null && selectedDefinition
      ? resolvePlacement(
          board.ships,
          placement.selectedShipId,
          placement.hoverIndex,
          placement.orientation,
        ).cells
      : null;
  const previewLegal = placement
    ? isBattleshipPlacementLegal(board.ships, placement.selectedShipId, previewCells)
    : false;
  const previewSet = new Set(previewCells ?? []);
  return (
    <section className={compact ? "naval-grid-panel compact" : "naval-grid-panel"}>
      <div className="naval-grid-title">
        <span>{targetable ? <Crosshair /> : <Ship />}</span>
        <div>
          <strong>{label}</strong>
          <small>
            {placement
              ? "Select a ship, rotate it, then tap or drag it onto the grid"
              : targetable
                ? "Choose an unmarked coordinate"
                : "Fleet and incoming fire"}
          </small>
        </div>
      </div>
      <div className="naval-axis naval-axis-x" aria-hidden="true">
        {"ABCDEFGHIJ".split("").map((letter) => (
          <span key={letter}>{letter}</span>
        ))}
      </div>
      <div className="naval-board-wrap">
        <div className="naval-axis naval-axis-y" aria-hidden="true">
          {navalRows.map((row) => (
            <span key={row}>{row}</span>
          ))}
        </div>
        <fieldset
          className={`naval-board${placement ? " placement-board" : ""}`}
          aria-label={label}
          onPointerLeave={() => placement?.onHover(null)}
        >
          {navalCells.map(({ index, coordinate: cellCoordinate }) => {
            const ship = shipsByCell.get(index);
            const shot = shotsByCell.get(index);
            const previewState = previewSet.has(index)
              ? previewLegal
                ? "placement-valid"
                : "placement-invalid"
              : "";
            const canInteract = Boolean(placement) || (targetable && !shot);
            return (
              <button
                type="button"
                className={[
                  "naval-cell",
                  ship ? "ship-cell" : "",
                  shot?.result ?? "",
                  shot?.result === "hit" ? "damaged" : "",
                  shot?.sunkShipId ? "sunk destroyed" : "",
                  previewState,
                ]
                  .filter(Boolean)
                  .join(" ")}
                aria-label={`${cellCoordinate}${
                  ship && placement ? `, ${ship.id}` : ""
                }${shot ? `, ${shot.result}${shot.sunkShipId ? ", ship sunk" : ""}` : ""}`}
                disabled={pending || !canInteract}
                draggable={Boolean(placement && ship && !pending)}
                data-battleship-cell={`${board.playerId}:${index}`}
                onPointerEnter={() => placement?.onHover(index)}
                onDragStart={(event) => {
                  if (!placement || !ship) return;
                  event.dataTransfer.effectAllowed = "move";
                  event.dataTransfer.setData("text/plain", ship.id);
                  placement.onSelectShip(ship.id);
                }}
                onDragEnter={() => placement?.onHover(index)}
                onDragOver={(event) => {
                  if (!placement) return;
                  event.preventDefault();
                  event.dataTransfer.dropEffect = "move";
                }}
                onDrop={(event) => {
                  if (!placement) return;
                  event.preventDefault();
                  const draggedShipId = event.dataTransfer.getData("text/plain");
                  const shipId = battleshipFleet.some(
                    (definition) => definition.id === draggedShipId,
                  )
                    ? (draggedShipId as BattleshipShipId)
                    : undefined;
                  placement.onPlaceShip(index, shipId);
                }}
                onClick={() => {
                  if (placement) {
                    if (ship) {
                      placement.onSelectShip(ship.id);
                      return;
                    }
                    placement.onPlaceShip(index);
                    return;
                  }
                  onFire(index);
                }}
                key={cellCoordinate}
              >
                {shot?.result === "hit" && (
                  <span
                    className={shot.sunkShipId ? "hit-marker destroyed-marker" : "hit-marker"}
                    aria-hidden="true"
                  />
                )}
                {shot?.result === "miss" && <span className="miss-marker" aria-hidden="true" />}
              </button>
            );
          })}
          <span className="naval-ships-layer">
            {board.ships.map((ship) => (
              <ShipSilhouette
                key={ship.id}
                ship={ship}
                ownerId={board.playerId}
                selected={placement?.selectedShipId === ship.id}
              />
            ))}
            {placement && previewCells && previewLegal && selectedDefinition && (
              <ShipSilhouette
                ownerId={`${board.playerId}:preview`}
                ship={{
                  id: selectedDefinition.id,
                  length: selectedDefinition.length,
                  cells: previewCells,
                  hits: [],
                  sunk: false,
                }}
                preview
              />
            )}
          </span>
          {pending && targetable && (
            <span className="naval-pending-dots" role="status" aria-label="Sending shot">
              <i />
              <i />
              <i />
            </span>
          )}
        </fieldset>
      </div>
    </section>
  );
}

function FleetStatus({
  board,
  selectedShipId,
  draggable = false,
  onSelect,
}: {
  board: BattleshipPublicBoard;
  selectedShipId?: BattleshipShipId;
  draggable?: boolean;
  onSelect?: (shipId: BattleshipShipId) => void;
}) {
  const [inspectedShipId, setInspectedShipId] = useState<BattleshipShipId | null>(null);
  const modalTitleId = useId();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const inspectedDefinition = inspectedShipId
    ? battleshipFleet.find((definition) => definition.id === inspectedShipId)
    : undefined;
  const inspectedShip = inspectedShipId
    ? board.ships.find((ship) => ship.id === inspectedShipId)
    : undefined;

  useEffect(() => {
    if (!inspectedShipId) return;
    closeButtonRef.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setInspectedShipId(null);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [inspectedShipId]);

  return (
    <>
      <ul
        className={draggable ? "fleet-status fleet-dock" : "fleet-status"}
        aria-label="Fleet status"
      >
        {battleshipFleet.map((definition) => {
          const ship = board.ships.find((candidate) => candidate.id === definition.id);
          const hits = ship?.hits.length ?? 0;
          return (
            <li
              className={[
                ship?.sunk ? "sunk" : "",
                draggable && !ship?.cells.length ? "unplaced" : "",
                draggable && ship?.cells.length ? "placed" : "",
                selectedShipId === definition.id ? "selected" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              key={definition.id}
            >
              <button
                type="button"
                className="fleet-status-trigger"
                draggable={draggable}
                aria-haspopup="dialog"
                aria-label={`View ${definition.name} status and capabilities`}
                onClick={() => {
                  onSelect?.(definition.id);
                  setInspectedShipId(definition.id);
                }}
                onDragStart={(event: DragEvent<HTMLButtonElement>) => {
                  if (!draggable) return;
                  event.dataTransfer.effectAllowed = "move";
                  event.dataTransfer.setData("text/plain", definition.id);
                  onSelect?.(definition.id);
                }}
              >
                <span
                  className={`fleet-miniature fleet-${definition.id}`}
                  style={{ "--mini-length": definition.length } as CSSProperties}
                  data-battleship-launch-source={`${board.playerId}:${definition.id}`}
                  aria-hidden="true"
                >
                  <img src={shipArtwork[definition.id]} alt="" />
                </span>
                <span className="fleet-copy">
                  <strong>{definition.name}</strong>
                  <small>
                    {ship?.sunk
                      ? "SUNK"
                      : draggable && !ship?.cells.length
                        ? `${definition.length} cells · not placed`
                        : draggable
                          ? `${definition.length} cells · placed`
                          : `${definition.length - hits}/${definition.length} decks`}
                  </small>
                </span>
                {draggable && <Grip aria-hidden="true" />}
              </button>
            </li>
          );
        })}
      </ul>
      {inspectedDefinition &&
        typeof document !== "undefined" &&
        createPortal(
          <div className="battleship-ship-modal">
            <button
              type="button"
              className="ship-intel-dismiss"
              aria-label="Close ship details"
              onClick={() => setInspectedShipId(null)}
            />
            <section
              className="ship-intel-card"
              role="dialog"
              aria-modal="true"
              aria-labelledby={modalTitleId}
            >
              <header>
                <span
                  className={`ship-intel-art fleet-${inspectedDefinition.id}`}
                  style={{ "--mini-length": inspectedDefinition.length } as CSSProperties}
                >
                  <img src={shipArtwork[inspectedDefinition.id]} alt="" />
                </span>
                <div>
                  <small>FLEET INTELLIGENCE</small>
                  <h3 id={modalTitleId}>{inspectedDefinition.name}</h3>
                  <p className="ship-intel-classification">
                    {shipClassDetails[inspectedDefinition.id].classification}
                  </p>
                </div>
                <button
                  ref={closeButtonRef}
                  type="button"
                  className="ship-intel-close"
                  aria-label="Close ship details"
                  onClick={() => setInspectedShipId(null)}
                >
                  <X />
                </button>
              </header>
              <div className="ship-intel-summary">
                <span
                  className={`ship-intel-status status-${getBattleshipShipStatus(
                    inspectedShip,
                    draggable,
                  )
                    .toLowerCase()
                    .replace(" ", "-")}`}
                >
                  <Activity />
                  {getBattleshipShipStatus(inspectedShip, draggable)}
                </span>
                <span>
                  {Math.max(0, inspectedDefinition.length - (inspectedShip?.hits.length ?? 0))}/
                  {inspectedDefinition.length} hull blocks intact
                </span>
              </div>
              <div
                className="ship-intel-hull"
                role="img"
                aria-label={`${inspectedShip?.hits.length ?? 0} of ${inspectedDefinition.length} hull blocks damaged`}
              >
                {navalHullBlocks.slice(0, inspectedDefinition.length).map((block, index) => (
                  <i
                    className={
                      inspectedShip?.sunk ||
                      index >= inspectedDefinition.length - (inspectedShip?.hits.length ?? 0)
                        ? "damaged"
                        : "intact"
                    }
                    key={block}
                  />
                ))}
              </div>
              <p className="ship-intel-role">{shipClassDetails[inspectedDefinition.id].role}</p>
              <dl className="ship-intel-specs">
                <div>
                  <dt>
                    <Grid3X3 /> Grid footprint
                  </dt>
                  <dd>{inspectedDefinition.length} connected blocks</dd>
                </div>
                <div>
                  <dt>
                    <Radar /> Signature attack
                  </dt>
                  <dd>{attackDetails[inspectedDefinition.id].label}</dd>
                </div>
              </dl>
              <div className="ship-intel-attack">
                <Crosshair />
                <p>
                  <strong>How it attacks</strong>
                  {attackDetails[inspectedDefinition.id].description}
                </p>
              </div>
              {inspectedShip?.sunk && (
                <p className="ship-intel-disabled">
                  This vessel has been destroyed and can no longer launch attacks.
                </p>
              )}
            </section>
          </div>,
          document.body,
        )}
    </>
  );
}

function BattleScore({
  playerId,
  targetBoard,
  participantId,
  points,
  rounds,
}: {
  playerId: string;
  targetBoard: BattleshipPublicBoard;
  participantId: string;
  points: number;
  rounds: number;
}) {
  const shots = targetBoard.shotsReceived;
  const hits = shots.filter((shot) => shot.result === "hit").length;
  const sunk = targetBoard.ships.filter((ship) => ship.sunk).length;
  const accuracy = shots.length === 0 ? 0 : Math.round((hits / shots.length) * 100);
  return (
    <article
      className={`battle-score-card${playerId === participantId ? " local-player" : ""}`}
      aria-label={`${playerLabel(playerId, participantId)} battle score`}
    >
      <span>{playerLabel(playerId, participantId)}</span>
      <strong>{points.toLocaleString()} pts</strong>
      <dl>
        <div>
          <dt>Hits</dt>
          <dd>
            {hits}/{shots.length}
          </dd>
        </div>
        <div>
          <dt>Accuracy</dt>
          <dd>{accuracy}%</dd>
        </div>
        <div>
          <dt>Sunk</dt>
          <dd>{sunk}/5</dd>
        </div>
        <div>
          <dt>Rounds</dt>
          <dd>{rounds}</dd>
        </div>
      </dl>
    </article>
  );
}

export function BattleshipBoard({
  state,
  participantId,
  pending,
  serverError,
  playAgain,
  onAction,
}: BattleshipBoardProps) {
  const ownBoard =
    state.boards.find((board) => board.playerId === participantId) ?? state.boards[0];
  const enemyBoard =
    state.boards.find((board) => board.playerId !== ownBoard?.playerId) ?? state.boards[1];
  const [selectedShipId, setSelectedShipId] = useState<BattleshipShipId>("carrier");
  const [orientation, setOrientation] = useState<BattleshipOrientation>("horizontal");
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [awaitingPlacedShipId, setAwaitingPlacedShipId] = useState<BattleshipShipId | null>(null);
  const [soundEnabled, setSoundEnabled] = useState(
    () =>
      typeof window === "undefined" ||
      window.localStorage.getItem("topzle:battleship-sfx") !== "off",
  );
  const currentShotKey = state.lastShot ? shotPresentationKey(state.lastShot) : null;
  const currentAttackStyle = state.lastShot ? selectAttackStyle(state, state.lastShot) : null;
  const impactDelayMs = Math.round(
    (currentAttackStyle ? attackDetails[currentAttackStyle].impactDelay : 1.2) *
      1_000 *
      (state.fastMode ? 0.45 : 1),
  );
  const [shotPresentation, setShotPresentation] = useState(() => {
    const settled =
      !currentShotKey ||
      state.actionAvailableAt <= (typeof window === "undefined" ? 0 : Date.now());
    return {
      key: currentShotKey,
      revealed: settled,
      complete: settled,
    };
  });
  const currentPlayerId = state.players[state.turnIndex];
  const isPlayer = state.players.includes(participantId);
  const ready = state.readyIds.includes(participantId);
  const shotRevealed =
    !currentShotKey || (shotPresentation.key === currentShotKey && shotPresentation.revealed);
  const shotSequenceComplete =
    !currentShotKey || (shotPresentation.key === currentShotKey && shotPresentation.complete);
  const humanTurn =
    state.phase === "playing" && currentPlayerId === participantId && shotSequenceComplete;
  const fleetComplete = Boolean(ownBoard && isBattleshipFleetComplete(ownBoard.ships));
  const audioActive = soundEnabled && state.phase !== "finished";

  useEffect(() => {
    void setBattleshipAudioEnabled(audioActive);
  }, [audioActive]);

  useEffect(
    () => () => {
      void setBattleshipAudioEnabled(false);
    },
    [],
  );

  useEffect(() => {
    if (!currentShotKey) {
      setShotPresentation({ key: null, revealed: true, complete: true });
      return;
    }

    const remaining = Math.max(0, state.actionAvailableAt - Date.now());
    if (remaining === 0) {
      setShotPresentation({
        key: currentShotKey,
        revealed: true,
        complete: true,
      });
      return;
    }

    setShotPresentation({
      key: currentShotKey,
      revealed: false,
      complete: false,
    });
    const revealTimer = window.setTimeout(
      () =>
        setShotPresentation((presentation) =>
          presentation.key === currentShotKey ? { ...presentation, revealed: true } : presentation,
        ),
      Math.min(impactDelayMs, remaining),
    );
    const completionTimer = window.setTimeout(() => {
      setShotPresentation((presentation) =>
        presentation.key === currentShotKey
          ? { ...presentation, revealed: true, complete: true }
          : presentation,
      );
    }, remaining);
    return () => {
      window.clearTimeout(revealTimer);
      window.clearTimeout(completionTimer);
    };
  }, [currentShotKey, impactDelayMs, state.actionAvailableAt]);

  useEffect(() => {
    const selectedShip = ownBoard?.ships.find((ship) => ship.id === selectedShipId);
    if (selectedShip?.cells.length) setOrientation(shipOrientation(selectedShip));
  }, [ownBoard, selectedShipId]);

  useEffect(() => {
    if (!ownBoard || !awaitingPlacedShipId) return;
    const placed = ownBoard.ships.find((ship) => ship.id === awaitingPlacedShipId);
    if (!placed?.cells.length) return;
    const next = ownBoard.ships.find((ship) => ship.cells.length === 0);
    if (next) {
      setSelectedShipId(next.id);
      setOrientation("horizontal");
    }
    setAwaitingPlacedShipId(null);
  }, [awaitingPlacedShipId, ownBoard]);

  const placement = useMemo<PlacementControls | undefined>(() => {
    if (!ownBoard || !isPlayer || ready || state.phase !== "placement") return undefined;
    return {
      selectedShipId,
      orientation,
      hoverIndex,
      onHover: (index) => {
        setHoverIndex(index);
        if (index === null) return;
        const resolved = resolvePlacement(ownBoard.ships, selectedShipId, index, orientation);
        if (resolved.legal && resolved.orientation !== orientation)
          setOrientation(resolved.orientation);
      },
      onSelectShip: (shipId) => {
        setSelectedShipId(shipId);
        setHoverIndex(null);
        const ship = ownBoard.ships.find((candidate) => candidate.id === shipId);
        if (ship) setOrientation(shipOrientation(ship));
      },
      onPlaceShip: (startIndex, draggedShipId) => {
        const shipId = draggedShipId ?? selectedShipId;
        const draggedShip = draggedShipId
          ? ownBoard.ships.find((ship) => ship.id === draggedShipId)
          : undefined;
        const preferredOrientation = draggedShip?.cells.length
          ? shipOrientation(draggedShip)
          : orientation;
        const resolved = resolvePlacement(ownBoard.ships, shipId, startIndex, preferredOrientation);
        if (!resolved.legal) return;
        if (resolved.orientation !== orientation) setOrientation(resolved.orientation);
        if (shipId !== selectedShipId) setSelectedShipId(shipId);
        onAction({
          type: "place_ship",
          shipId,
          startIndex,
          orientation: resolved.orientation,
        });
        setAwaitingPlacedShipId(shipId);
        setHoverIndex(null);
      },
    };
  }, [hoverIndex, isPlayer, onAction, orientation, ownBoard, ready, selectedShipId, state.phase]);

  if (!ownBoard || !enemyBoard) return null;

  const presentedOwnBoard = concealUnresolvedShot(ownBoard, state.lastShot, shotRevealed);
  const presentedEnemyBoard = concealUnresolvedShot(enemyBoard, state.lastShot, shotRevealed);
  const status =
    state.phase === "placement"
      ? ready
        ? "Fleet locked · waiting for your rival"
        : `Deploy your fleet · ${ownBoard?.ships.filter((ship) => ship.cells.length > 0).length ?? 0}/5 ships placed`
      : !shotSequenceComplete
        ? shotRevealed
          ? "Damage confirmed · completing strike report"
          : "Strike in flight · awaiting impact"
        : state.phase === "finished"
          ? state.winnerId === participantId
            ? "Enemy fleet destroyed · you win!"
            : `${playerLabel(state.winnerId ?? "", participantId)} wins the battle`
          : humanTurn
            ? "Weapons ready · choose enemy coordinates"
            : currentPlayerId?.startsWith("ai:")
              ? "Topzle AI is calculating a firing solution"
              : "Incoming threat · opponent is targeting";
  const lastShotText =
    state.lastShot && shotRevealed
      ? state.lastShot.sunkShipId
        ? `${coordinate(state.lastShot.index)} · ${state.lastShot.sunkShipId} destroyed`
        : `${coordinate(state.lastShot.index)} · ${state.lastShot.result}`
      : null;

  return (
    <div className="active-game battleship-game">
      <GlobalShotSequence
        key={
          state.lastShot
            ? `${state.lastShot.attackerId}-${state.lastShot.defenderId}-${state.lastShot.index}`
            : "no-shot"
        }
        shot={state.lastShot}
        state={state}
        participantId={participantId}
        soundEnabled={audioActive}
      />
      <div className={`battleship-layout phase-${state.phase}`}>
        <aside className="battleship-command-column">
          <div className="battleship-command-head">
            <div className="game-status battleship-status">
              <div className="battleship-roundline">
                <span>BATTLESHIP</span>
                <b>ROUND {String(state.round).padStart(2, "0")}</b>
              </div>
              <strong>{status}</strong>
            </div>
            <div className="battleship-quick-controls">
              {participantId === state.players[0] && (
                <button
                  type="button"
                  className={state.fastMode ? "battleship-fast active" : "battleship-fast"}
                  aria-pressed={state.fastMode}
                  disabled={pending || state.phase === "finished"}
                  onClick={() => onAction({ type: "set_fast_mode", enabled: !state.fastMode })}
                >
                  <Gauge />
                  <span>{state.fastMode ? "Fast on" : "Fast mode"}</span>
                </button>
              )}
              <button
                type="button"
                className={audioActive ? "battleship-sfx active" : "battleship-sfx"}
                aria-pressed={audioActive}
                disabled={state.phase === "finished"}
                aria-label={
                  state.phase === "finished"
                    ? "Battleship audio stopped because the battle ended"
                    : soundEnabled
                      ? "Mute Battleship effects and background music"
                      : "Enable Battleship effects and background music"
                }
                title={
                  soundEnabled ? "Mute battle sounds and music" : "Enable battle sounds and music"
                }
                onClick={() => {
                  const enabled = !soundEnabled;
                  setSoundEnabled(enabled);
                  window.localStorage.setItem("topzle:battleship-sfx", enabled ? "on" : "off");
                  void setBattleshipAudioEnabled(enabled);
                }}
              >
                {audioActive ? <Volume2 /> : <VolumeX />}
                <span className="battleship-sfx-copy">
                  {state.phase === "finished"
                    ? "Battle ended"
                    : soundEnabled
                      ? "Music + SFX"
                      : "All muted"}
                </span>
              </button>
            </div>
          </div>
          {state.phase !== "placement" && (
            <div className="battle-scoreboard">
              <BattleScore
                playerId={ownBoard.playerId}
                targetBoard={presentedEnemyBoard}
                participantId={participantId}
                points={state.battleScores[ownBoard.playerId] ?? 0}
                rounds={state.scores[ownBoard.playerId] ?? 0}
              />
              <div className="battle-versus" aria-hidden="true">
                <LocateFixed />
                <span>VS</span>
              </div>
              <BattleScore
                playerId={enemyBoard.playerId}
                targetBoard={presentedOwnBoard}
                participantId={participantId}
                points={state.battleScores[enemyBoard.playerId] ?? 0}
                rounds={state.scores[enemyBoard.playerId] ?? 0}
              />
            </div>
          )}
          {state.lastBotInsight && (
            <div className="bot-tactical-read" role="status">
              <LocateFixed />
              <span>
                <strong>AI tactical read · {state.lastBotInsight.confidence}%</strong>
                {state.lastBotInsight.reason}
              </span>
            </div>
          )}
          <div
            className={
              serverError || lastShotText ? "battleship-feedback visible" : "battleship-feedback"
            }
            role="status"
          >
            {serverError ?? lastShotText ?? " "}
          </div>
          {state.phase === "finished" && shotSequenceComplete && (
            <div className="battleship-actions">{playAgain}</div>
          )}
        </aside>
        {state.phase === "placement" ? (
          <div className="battleship-deployment">
            <NavalGrid
              board={ownBoard}
              label="Your classified waters"
              targetable={false}
              pending={pending}
              placement={placement}
              onFire={() => undefined}
            />
            <aside className="deployment-console">
              <div className="deployment-heading">
                <span className="deployment-icon">
                  <Waves />
                  <Ship />
                </span>
                <div>
                  <span className="eyebrow">FLEET COMMAND</span>
                  <h3>{ready ? "Deployment locked" : "Build your formation"}</h3>
                </div>
              </div>
              <p>
                Select any vessel from this list or directly on the board, then tap or drag it to
                its new position. Your fleet stays classified until a ship is destroyed.
              </p>
              <FleetStatus
                board={ownBoard}
                selectedShipId={selectedShipId}
                draggable={!ready}
                onSelect={(shipId) => placement?.onSelectShip(shipId)}
              />
              {isPlayer && !ready && (
                <>
                  <button
                    type="button"
                    className="orientation-control"
                    disabled={pending}
                    onClick={() =>
                      setOrientation((value) =>
                        value === "horizontal" ? "vertical" : "horizontal",
                      )
                    }
                  >
                    <RotateCw /> Rotate selected ship
                    <span>{orientation}</span>
                  </button>
                  <div className="deployment-actions">
                    <button
                      type="button"
                      className="button button-ghost"
                      disabled={pending}
                      onClick={() => onAction({ type: "shuffle" })}
                    >
                      {pending ? <LoaderCircle className="spin" /> : <Shuffle />} Auto deploy
                    </button>
                    <button
                      type="button"
                      className={`button button-primary${fleetComplete && !pending ? " ready-to-lock" : ""}`}
                      disabled={pending || !fleetComplete}
                      onClick={() => {
                        if (soundEnabled) void setBattleshipAudioEnabled(true);
                        onAction({ type: "ready" });
                      }}
                    >
                      {pending ? <LoaderCircle className="spin" /> : <ShieldCheck />} Lock & ready
                    </button>
                  </div>
                  {!fleetComplete && (
                    <small className="deployment-progress">
                      Place all five ships to unlock “Lock & ready”.
                    </small>
                  )}
                </>
              )}
              {ready && (
                <div className="fleet-locked">
                  <ShieldCheck /> Coordinates encrypted and locked.
                </div>
              )}
            </aside>
          </div>
        ) : (
          <div className="battleship-combat">
            <div className="battleship-target">
              <NavalGrid
                board={presentedEnemyBoard}
                label="Enemy waters"
                targetable={humanTurn}
                pending={pending || !shotSequenceComplete}
                onFire={(index) => {
                  if (soundEnabled) void setBattleshipAudioEnabled(true);
                  onAction({ type: "fire", index });
                }}
              />
              <FleetStatus board={presentedEnemyBoard} />
            </div>
            <div className="battleship-own">
              <NavalGrid
                board={presentedOwnBoard}
                label="Your fleet"
                targetable={false}
                pending={pending}
                compact
                onFire={() => undefined}
              />
              <FleetStatus board={presentedOwnBoard} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
